<script setup lang="ts">
import type { PropertyColor, PropertySize } from "~/classes/generated/interface"

import starFull from "~/assets/icons/rating/star-full.svg"
import starHalf from "~/assets/icons/rating/star-half.svg"
import { Property, PropertySchema } from "~/classes/entity"

const props = defineProps({
  file: null,
  size: null,
  color: null,
  selected: { type: Boolean },
  editable: { type: Boolean }
})

const { viewModePropertySizes, viewModePropertyColors } = useViewMode()
const { updateFileProperty } = useFileSystem()
const newRating = ref(0)

const resetNewRating = () => {
  newRating.value = 0
}

const rating = computed(() => props.file.rating() ?? 0)

/** show full star if i >  */
const showFull = (i: number) => {
  if (newRating.value > 0)
    return newRating.value >= i + 1
  else
    return rating.value >= i + 1
}

const showHalf = (i: number) => {
  if (newRating.value > 0)
    return newRating.value >= (i + 0.5)
  else
    return rating.value >= (i + 0.5)
}

const setNewRating = (event: MouseEvent) => {
  if (!props.editable)
    return
  const target = event.currentTarget as HTMLElement

  const rect = target.getBoundingClientRect()
  const offsetX = event.clientX - rect.left
  const width = rect.width
  const rating = (offsetX * 10.0) / width
  const remainder = rating % 1
  if (remainder >= 0.66) {
    newRating.value = Math.ceil(rating)
  }
  else if (remainder >= 0.33) {
    newRating.value = Math.floor(rating) + 0.5
  }
  else {
    newRating.value = Math.floor(rating)
  }
}

const updateRating = () => {
  if (newRating.value === 0)
    return
  else if (newRating.value === rating.value)
    return console.debug("Selected rating equals current rating, skipping update")
  updateFileProperty(
    props.file,
    Property.FILE_RATING,
    { type: PropertySchema.PositiveFloatValue, positiveFloat: newRating.value },
  )
}
</script>

<template>
  <div class="container">
    <div
      v-if="editable"
      class="rating"
      :class="{
        [viewModePropertyColors[color].textCss]: true,
        [viewModePropertySizes[size].label]: true,
        editable,
      }"
      @mousemove="(event) => setNewRating(event)"
      @mouseleave="resetNewRating()"
      @click="updateRating()"
    >
      <template v-for="key in range(10)" :key>
        <div class="star">
          <star-full v-if="showFull(key)" class="star-icon" />
          <star-half v-else-if="showHalf(key)" class="star-icon" />
        </div>
      </template>
    </div>
    <div
      v-else
      class="rating display"
      :class="{
        [viewModePropertySizes[size].label]: true,
        [viewModePropertyColors[color].textCss]: true,
      }"
    >
      <template v-for="key in range(Math.ceil(rating))" :key>
        <div class="star">
          <star-full v-if="showFull(key)" />
          <star-half v-else-if="showHalf(key)" />
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.container
  @apply px-1 py-0.5 inline-flex items-center
.rating
  @apply text-ellipses-inline rounded-[#{$page-properties-br-overlay}]
  @apply inline-flex items-center flex-none
  &.editable
    @apply cursor-pointer
  .star
    @apply flex-none flex items-center
  &.Giant .star
    @apply w-8 h-8 m-0.25
  &.Huge .star
    @apply w-6 h-6 m-0.25
  &.Large .star
    @apply w-5 h-5 m-0.25
  &.Medium .star
    @apply w-4 h-4 m-0.25
  &.Small .star
    @apply w-3 h-3 m-0.1
  &.Tiny .star
    @apply w-2.5 h-2.5 m-0.1

.star-icon
  @apply w-full h-full // fixes a bug on safari with viewing svg images

.display
  @apply flex-wrap
</style>
